<template>
  <div class="query-history-card">
    <div class="qh-header">
      <div class="qh-title">Query History</div>
      <el-tag
        v-if="openThread"
        size="small" type="danger" effect="plain">Open</el-tag>
      <el-tag
        v-else
        size="small" type="info" effect="plain">No Open Query</el-tag>
    </div>

    <!-- history list -->
    <div class="qh-list" ref="outerList">
      <div v-if="normalized.length === 0" class="empty muted">
        No previous queries for this invoice.
      </div>

      <div v-for="q in normalized" :key="q.id" class="thread">
        <div class="thread-head">
          <div class="left">
            <span class="muted tiny">Reason:</span>
            <span class="reason">{{ humanize(q.reason_code) }}</span>
          </div>
          <el-tag :type="q.resolved ? 'info' : 'danger'" size="mini" effect="plain">
            {{ q.resolved ? 'Closed' : 'Open' }}
          </el-tag>
        </div>

        <div class="thread-body" :ref="setThreadRef(q.id)">
          <template v-for="(items, label) in groupedByDay(q.messages)">
            <div :key="q.id + '-' + label" class="day-sep">
              <span class="chip">{{ label }}</span>
            </div>

            <div v-for="m in items" :key="m.id"
                 class="msg-row" :class="{ mine: !isUser(m), theirs: isUser(m) }">
              <div class="bubble" :class="{ mine: !isUser(m), theirs: isUser(m) }">
                <div class="from tiny">{{ isUser(m) ? 'User' : 'Finance' }}</div>
                <div class="body">{{ m.body }}</div>
                <div class="time tiny muted">{{ formatTime(m.created_at) }}</div>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>

    <!-- reply to the latest open thread -->
    <div v-if="openThread" class="reply-panel">
      <div class="muted tiny mb4">Replying to the latest open query</div>
      <el-input type="textarea" :rows="4" v-model="reply" placeholder="Type your reply…"/>
      <div class="actions">
        <el-button
          class="ml8"
          type="success"
          :loading="resolving"
          :disabled="resolving"
          @click="resolveThread"
        >
          Resolve
        </el-button>
        <el-button type="primary" :loading="submitting"
                   :disabled="!reply.trim() || submitting"
                   @click="sendReply">Send Reply</el-button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from '@/plugins/axios'

export default {
  name: 'AdminInvoiceQueryHistory',
  props: {
    invoiceId: { type: [Number, String], required: true },
    /** pass the array you get at invoice.queries */
    queries: { type: Array, default: () => [] },
    visible: { type: Boolean, default: true },
  },
  data () {
    return {
      normalized: [],
      reply: '',
      submitting: false,
      threadRefs: {},
      resolve: false,
    }
  },
  watch: {
    queries: {
      immediate: true,
      handler (list) {
        const norm = (list || []).map(q => {
          const messages = this.normalizeMessages(q)
          return {
            id: q.id,
            reason_code: q.reason_code || '',
            resolved: !!q.resolved,
            created_at: q.created_at,
            messages
          }
        })
        norm.sort((a, b) => +new Date(a.created_at) - +new Date(b.created_at))
        this.normalized = norm
        this.$nextTick(this.scrollAll)
      }
    },
    visible (v) { if (v) this.$nextTick(this.scrollAll) }
  },
  computed: {
    latest () {
      if (!this.normalized.length) return null
      return this.normalized[this.normalized.length - 1]
    },
    openThread () {
      return this.latest.resolved !== 1
    }
  },
  methods: {
    // --- formatting/helpers ---
    humanize (s) { return (s || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) },
    formatTime (iso) { const d = new Date(iso); return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) },
    dayLabel (iso) {
      const d = new Date(iso), t = new Date(), y = new Date(); y.setDate(t.getDate() - 1)
      const same = (a,b)=>a.getFullYear()===b.getFullYear()&&a.getMonth()===b.getMonth()&&a.getDate()===b.getDate()
      if (same(d,t)) return 'Today'; if (same(d,y)) return 'Yesterday'; return d.toLocaleDateString()
    },
    groupedByDay (msgs) {
      const g = {}
      ;(msgs || []).forEach(m => { const k = this.dayLabel(m.created_at); (g[k]=g[k]||[]).push(m) })
      return g
    },
    isUser (m) { return typeof m.sender_id === 'number' },

    normalizeMessages (q) {
      const msgs = Array.isArray(q.messages) ? [...q.messages] : []
      // if BE also puts the first user message into q.message, and messages already contain it, do nothing
      if (q.message && !msgs.length) {
        msgs.unshift({
          id: -Number(q.id),
          invoice_query_id: q.id,
          sender_id: q.user_id,
          body: q.message,
          created_at: q.created_at,
          updated_at: q.created_at
        })
      }
      msgs.sort((a, b) => +new Date(a.created_at) - +new Date(b.created_at))
      return msgs
    },

    // --- scrolling ---
    setThreadRef (id) { return (el) => { this.threadRefs[id] = el } },
    scrollAll () {
      const outer = this.$refs.outerList
      if (outer) outer.scrollTop = outer.scrollHeight
      const tid = (this.openThread && this.openThread.id) || (this.latest && this.latest.id)
      if (tid && this.threadRefs[tid]) this.threadRefs[tid].scrollTop = this.threadRefs[tid].scrollHeight
    },

    // --- actions ---
    async sendReply () {
      if (!this.reply.trim() || !this.openThread) return
      this.submitting = true
      try {
        // If your BE replies to "latest open", this is enough:
        const { data } = await axios.admin.post(`/invoices/${this.invoiceId}/query/reply`, {
          message: this.reply,
          // If your BE requires thread id, include: thread_id: this.openThread.id
        })
        const updated = (data && data.query) ? data.query : data
        if (updated && updated.id) {
          // merge
          const copy = [...this.normalized]
          const idx = copy.findIndex(q => q.id === updated.id)
          const normalized = {
            id: updated.id,
            reason_code: updated.reason_code || '',
            resolved: !!updated.resolved,
            created_at: updated.created_at,
            messages: this.normalizeMessages(updated)
          }
          if (idx >= 0) copy.splice(idx, 1, normalized)
          else copy.push(normalized)
          copy.sort((a,b)=>+new Date(a.created_at)-+new Date(b.created_at))
          this.normalized = copy
          this.$nextTick(this.scrollAll)
        }
        this.reply = ''
        this.$emit('updated')
        this.$message.success('Reply sent.')
      } finally {
        this.submitting = false
      }
    },

    async resolveThread () {
      if (!this.openThread) return
      try {
        await this.$confirm(
          'Mark the latest open query as resolved?',
          'Confirm',
          { type: 'warning', confirmButtonText: 'Yes', cancelButtonText: 'No' }
        )
      } catch (e) { return }

      this.resolving = true
      try {
        await axios.admin.post(`/invoices/${this.invoiceId}/query/resolve`, {
          resolution_notes: this.reply && this.reply.trim() ? this.reply.trim() : undefined
        })

        const copy = [...this.normalized]
        if (copy.length) {
          copy[copy.length - 1] = {
            ...copy[copy.length - 1],
            resolved: true
          }
          this.normalized = copy
        }

        this.$message.success('Query marked as resolved.')
        this.reply = ''
        this.$emit('updated')
        this.$nextTick(this.scrollAll)
      } finally {
        this.resolving = false
      }
    }
  }
}
</script>

<style scoped>
.query-history-card { margin-top:14px; border:1px solid #e5e7eb; border-radius:12px; background:#fff; padding:12px; }
.qh-header { display:flex; align-items:center; justify-content:space-between; }
.qh-title { font-weight:700; font-size:14px; color:#111827; }
.qh-list { margin-top:10px; max-height:360px; overflow-y:auto; display:grid; gap:12px; }
.empty { text-align:center; padding:16px 0; }
.thread { border:1px solid #e5e7eb; border-radius:10px; background:#fafafa; }
.thread-head { display:flex; align-items:center; justify-content:space-between; padding:8px 10px; border-bottom:1px solid #e5e7eb; background:#f8fafc; }
.reason { font-weight:700; margin-left:6px; }
.left { display:flex; align-items:center; gap:6px; }
.thread-body { max-height:260px; overflow-y:auto; padding:10px 10px 12px; display:grid; gap:10px; }
.day-sep { display:flex; justify-content:center; }
.chip { font-size:11px; color: #000000; background:#eef2ff; padding:2px 8px; border-radius:999px; border:1px solid #e5e7eb; }
.msg-row { display:flex; }
.msg-row.mine { justify-content:flex-end; }
.msg-row.theirs { justify-content:flex-start; }
.bubble { max-width:75%; border-radius:16px; padding:10px 12px; box-shadow:0 1px 3px rgba(0,0,0,.05); }
.bubble.mine { background:#E9004C; color:#fff; border-bottom-right-radius:6px; }
.bubble.theirs { background: #dedede; color: #000000; border-bottom-left-radius:6px; }
.from { font-weight:700; margin-bottom:2px; opacity:.9; }
.body { white-space:pre-wrap; }
.time { margin-top:4px; text-align:right; }
.muted { color: #000000; }
.tiny { font-size:12px; }
.mb4 { margin-bottom:4px; }
.reply-panel { margin-top:12px; border-top:1px dashed #e5e7eb; padding-top:10px; }
.actions { margin-top:8px; display:flex; justify-content:flex-end; }
</style>